// The §6 comment surface (issue #53): ordinary same-origin HTTP, cookie-
// carried identity — the same pattern as /session (net/session.ts). See
// cmd/wwp/comment.go for why this is HTTP rather than a fifth §3 wire class.

export interface CommentView {
  id: number;
  body: string;
  country_id: number;
  created_at: string;
  kind: 'live' | 'history' | 'mine';
  /** Only present when kind is 'mine' — §6's asymmetry, see comment.go. */
  state?: string;
}

export interface PixelCommentsResponse {
  pixel_id: number;
  live?: CommentView;
  history?: CommentView[];
  mine?: CommentView;
}

export interface CommentsOptions {
  fetchImpl?: typeof fetch;
}

/** §6's cap, mirrored client-side purely for the input counter. The server's
 * internal/comment.ValidateBody is the actual authority. */
export const MaxCommentRunes = 255;

/** GET a pixel's visible comment(s), scoped to whoever's cookie is presented. */
export async function fetchPixelComments(
  pixel: number,
  opts: CommentsOptions = {},
): Promise<PixelCommentsResponse> {
  const f = opts.fetchImpl ?? fetch;
  const res = await f(`/pixel/${pixel}/comment`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`net: GET /pixel/${pixel}/comment answered ${res.status}`);
  return (await res.json()) as PixelCommentsResponse;
}

export class CommentSubmitError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * POST a comment for the pixel this identity most recently captured.
 *
 * The server is the only real authority on whether that is true (see
 * internal/comment/capture.go) — this call may come back 403 for a pixel the
 * caller only looked at, and that is not a bug to route around client-side.
 */
export async function submitComment(
  pixel: number,
  body: string,
  opts: CommentsOptions = {},
): Promise<{ id: number; state: string }> {
  const f = opts.fetchImpl ?? fetch;
  const res = await f('/comment', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ pixel_id: pixel, body }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new CommentSubmitError(res.status, text || `submit answered ${res.status}`);
  }
  return (await res.json()) as { id: number; state: string };
}
